import { makeObservable, observable, action, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';
import BaseAdminStore, { AdminApiResponse } from '../../../shared/stores/baseAdminStore';
import { MatenRequestResponse } from '../../../shared';

export interface DocumentTemplate {
    id: number;
    name: string;
    html_content: string;
    created_at: string;
    updated_at: string;
}

export class DocumentTemplateStore {
    public id: number;
    public name: string;
    public html_content: string;
    public created_at: string;
    public updated_at: string;

    constructor(template: DocumentTemplate) {
        makeObservable(this, {
            id: observable,
            name: observable,
            html_content: observable,
            created_at: observable,
            updated_at: observable,
        });

        this.id = template.id;
        this.name = template.name;
        this.html_content = template.html_content;
        this.created_at = template.created_at;
        this.updated_at = template.updated_at;
    }

    public async edit(sdk: ApplicationSdk, store: DocumentAdminStore): Promise<void> {
        const templateEditResponse = await sdk.getDialogMultiInput({
            title: 'Template bearbeiten',
            fields: [
                {
                    label: 'Template Name',
                    key: 'name',
                    type: 'text',
                },
            ],
            initialValue: {
                name: this.name,
            },
            message: 'Hier können Sie die Einstellungen des Templates bearbeiten.',
            onConfirm: async (values: any) =>
                store.saveTemplate(this.id, values.name, this.html_content),
        });

        if (templateEditResponse.confirmed) {
            sdk.showSuccessToast('Template erfolgreich aktualisiert');
        } else if (templateEditResponse.error) {
            sdk.showErrorToast(templateEditResponse.error);
        }
    }
}

class DocumentAdminStore extends BaseAdminStore {
    public templates: DocumentTemplateStore[] = [];
    public selectedTemplate: DocumentTemplateStore | null = null;

    constructor(sdk: ApplicationSdk) {
        super(sdk);
        makeObservable(this, {
            templates: observable,
            selectedTemplate: observable,
            selectTemplate: action,
            saveTemplate: action,
            createTemplate: action,
            deleteTemplate: action,
            saveTemplateContent: action,
        });
    }

    protected getApiEndpoint(): string {
        return 'signabledocuments';
    }

    protected getApiPath(): string {
        return 'admin/templates';
    }

    protected buildApiParams(): any {
        return {
            method: 'POST',
            body: {},
        };
    }

    protected processApiResponse(response: AdminApiResponse): any {
        if (response.status === 200) {
            const templates = response.json.data || [];
            runInAction(() => {
                this.templates = templates.map(
                    (template: DocumentTemplate) => new DocumentTemplateStore(template)
                );
            });
            return templates;
        }
        return null;
    }

    public selectTemplate(template: DocumentTemplateStore | null): void {
        this.selectedTemplate = template;
    }

    public async saveTemplate(
        id: number,
        name: string,
        html_content: string
    ): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('signabledocuments', 'admin/templates/update', {
                id,
                name,
                html_content,
            });

            if (response.status === 200) {
                await this.fetchData();
                return { success: true };
            } else {
                return { success: false, error: 'Fehler beim Speichern des Templates' };
            }
        } catch (error) {
            console.error('Error saving template:', error);
            return { success: false, error: 'Netzwerkfehler beim Speichern des Templates' };
        }
    }

    public async createTemplate(
        name: string,
        html_content: string = ''
    ): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('signabledocuments', 'admin/templates/create', {
                name,
                html_content,
            });

            if (response.status === 200) {
                await this.fetchData();
                return { success: true, data: response.json };
            } else {
                return { success: false, error: 'Fehler beim Erstellen des Templates' };
            }
        } catch (error) {
            console.error('Error creating template:', error);
            return { success: false, error: 'Netzwerkfehler beim Erstellen des Templates' };
        }
    }

    public async deleteTemplate(id: number): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('signabledocuments', 'admin/templates/delete', {
                id,
            });

            if (response.status === 200) {
                await this.fetchData();
                return { success: true };
            } else {
                return { success: false, error: 'Fehler beim Löschen des Templates' };
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            return { success: false, error: 'Netzwerkfehler beim Löschen des Templates' };
        }
    }

    public async saveTemplateContent(
        id: number,
        html_content: string
    ): Promise<MatenRequestResponse> {
        const template = this.templates.find(t => t.id === id);
        if (!template) {
            return { success: false, error: 'Template nicht gefunden' };
        }

        return await this.saveTemplate(id, template.name, html_content);
    }
}

export default DocumentAdminStore;
